import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import {
    CodeBlock, BlockType, SettingType, ValidationMessage, SettingState, ValidationMessageType,
} from '@appblocks/codegeneration/types';
import { TIBBO_DEVICE_ID } from '@appblocks/platforms/PlatformUtils';
import LUISGroup from '@appblocks/codegeneration/SettingsConfigurations/LUISGroup';

export default class LCDMenu extends BaseFeature {
    constructor() {
        super();
        this.name = 'LCD Menus';
        this.id = 'lcd_menu';
        this.icon = 'CgScreen';
        this.requirements = [['TPP2W(G2)', 'TPP2W(G3)']];
        this.fileLinks = [

        ];
        this.files = [

        ];

        const settings = [
            {
                id: 'ENABLED',
                name: 'LCD Menus Enabled',
                settingType: SettingType.OPTION,
                required: true,
                default: '0',
                options: [
                    {
                        name: 'Disabled',
                        value: '0',
                    },
                    {
                        name: 'Enabled',
                        value: '1',
                    },
                ],
            },
            {
                id: 'GROUPS',
                name: 'Settings Groups',
                settingType: SettingType.ARRAY,
                refId: 'luis_group',
                required: true,
                default: [
                    new LUISGroup(),
                ],
            },
        ];

        this.settings = this.settings.concat(settings);
    }

    getDependencies() {
        const dependencies = [
            'stg',
            'kp',
        ];

        return dependencies;
    }

    getFiles(): Array<string> {
        let { files } = this;

        files = files.concat([
            'libraries/ui/ui_input_box.tbh',
            'libraries/ui/ui_input_box.tbs',
            'libraries/ui/ui_list_box.tbh',
            'libraries/ui/ui_list_box.tbs',
            'libraries/ui/ui_msg_box.tbh',
            'libraries/ui/ui_msg_box.tbs',
            'libraries/ui/ui_menu.tbh',
            'libraries/ui/ui_menu.tbs',
            'libraries/ui/ui.tbh',
            'libraries/ui/ui.tbs',
            'menu.xtxt',
            'icon.bmp',
        ]);

        return files;
    }

    validate(): Array<ValidationMessage> {
        const errors: Array<ValidationMessage> = [];

        const groups = this.getSettingValue('GROUPS');

        const { project } = this;

        const boardSettingFeature = project.getFeature('boardsetting');
        const TPB = boardSettingFeature.getSettingValue('TPB');
        if (project.device.id === TIBBO_DEVICE_ID.TPP2WG2) {
            if (TPB !== 'tpb2lg2') {
                errors.push({
                    messageType: ValidationMessageType.UPDATE,
                    message: '',
                    featureId: boardSettingFeature.getID(),
                    settingId: 'TPB',
                    data: 'tpb2lg2',
                });
            }
        }
        return errors;
    }

    getState(settingId: string): SettingState {
        if (settingId !== 'ENABLED' && this.getSettingValue('ENABLED').toString() === '0') {
            return SettingState.HIDDEN;
        }
        switch (settingId) {
            case 'ENABLED':
                return SettingState.ENABLED;
            case 'LCDENABLED': {
                const { project } = this;
                if (project.device.id === 'TPP2W(G2)') {
                    return SettingState.ENABLED;
                }
                return SettingState.HIDDEN;
            }
            default:
                return SettingState.ENABLED;
        }
    }

    getCodeBlocks() {
        const blocks: Array<CodeBlock> = [];
        // moved to LCD
        return blocks;
        if (this.getSettingValue('ENABLED').toString() === '0') {
            return blocks;
        }

        const settingsFile = '';
        let tmpBlocks: Array<CodeBlock> = [];
        const groups = this.getSettingValue('GROUPS');
        let sdfFile = '';
        let luisFile = '';
        let menuFile = '';
        for (let i = 0; i < groups.length; i += 1) {
            const group = groups[i];
            tmpBlocks = tmpBlocks.concat(group.getCodeBlocks());
        }
        for (let i = 0; i < tmpBlocks.length; i += 1) {
            switch (tmpBlocks[i].location) {
                case 'luis.xtxt':
                    luisFile += tmpBlocks[i].template;
                    break;
                case 'sdf.xtxt':
                    sdfFile += tmpBlocks[i].template;
                    break;
                case 'menu.xtxt':
                    menuFile += tmpBlocks[i].template;
                    break;
                default:
                    sdfFile += tmpBlocks[i].template;
                    luisFile += tmpBlocks[i].template;
                    break;
            }
        }


        if (groups.length === 1) {
            const lines = menuFile.split('\n');
            for (let i = 0; i < lines.length; i += 1) {
                if (lines[i].trim().length === 0) {
                    lines.splice(i, 1);
                    i -= 1;
                }
            }
            lines.shift();
            for (let i = 0; i < lines.length; i += 1) {
                // set menu level to 0
                lines[i] = `${lines[i].substr(0, 4)}0${lines[i].substr(5)}`;
            }
            menuFile = lines.join('\n');
        }

        blocks.push({
            location: 'DEVICE_TBS',
            blockType: BlockType.CODE,
            template: `
sub ui_menu_exit()
    '****ui_menu_exit****'
end sub`,
        });

        blocks.push({
            location: 'on_kp',
            blockType: BlockType.CODE,
            template: `
    if ui_active=true then
        ui_handle_kp(key_event,key_code)
    end if`,
        });

        blocks.push({
            location: 'MENU_XTXT',
            blockType: BlockType.CODE,
            template: `
Acc\tMen\tItem\tItem\tItem
lvl\tlvl\ttype\tString\tData
${menuFile}\r\n`,
        });
        blocks.push({
            location: 'GLOBAL_DEFINES',
            blockType: BlockType.CODE,
            template: `
#define MENU_CURSOR_STYLE 1
#define LIST_BOX_CURSOR_STYLE 1
#define INPUT_BOX_CURSOR_STYLE 1
#define LCD_LOCK lcd.lock
#define LCD_UNLOCK lcd.unlock
#define VER_SPACING 9
#define HOR_SPACING 2 

#define BMP_LEFT_XOFFSET 320
#define BMP_NUM_XOFFSET 240
#define BMP_ALPH1_XOFFSET 280
#define BMP_ALPH2_XOFFSET 360
#define BMP_STORE_XOFFSET 40

#define STD_INPUT_WAIT_TIME 20 'in 0.5 seconds

'------ ui_menu ------
#define BMP_CANCEL_XOFFSET 0
#define BMP_ENTER_XOFFSET 40
#define BMP_UP_XOFFSET 80
#define BMP_DOWN_XOFFSET 120
#define MENU_MAX_LVL 5
#define MENU_MAX_ITEM_PER_LVL 16
#define MENU_DISPLAY_ITEM_PER_LVL 6
#define LCD_MENU_TOUT 0
#define DISPLAY_STR_LEN 150
#define MENU_STRING_LEN 255
#define SCROLL_BAR_WIDTH 15
#define SCROLL_ARROW_HEIGHT 12
#define SCROLL_MIN_HEIGHT 2
#define SCROLL_CURSOR_WIDTH 9
#define CURSOR_BAR_LEN_OFFSET 2
#define SCROLL_BORDER 2
#define LCD_BACKCOLOR 0
#define LCD_FORECOLOR 65535
#define LCD_DISABLE_BACKCOLOR 14823
#define LCD_DISABLE_FORECOLOR 33840
#define LCD_CURSORCOLOR 63080
#define LCD_DEAD_SCREEN_COLOR 63488
#define MENU_X 0
#define MENU_Y 7
#define MENU_W 320
#define MENU_H 187
#define MENU_MAX_LINE 6
#define UM_HOR_SPACING HOR_SPACING
#define UM_VER_SPACING VER_SPACING
#define MENU_SCRIPT_FILE "menu.xtxt"
#define USE_STG_TIMESTAMP 0

'------ ui_msg_box ------
#define MSG_BOX_X 0
#define MSG_BOX_Y 0
#define MSG_BOX_W lcd.width
#define MSG_BOX_H 185
#define MSB_HOR_SPACING 2
#define MSB_VER_SPACING 3

'------ ui_input_box -----
#define IB_MAX_LEN 64
#define LCD_FIX_FONT "11X19(H).bin"
#define INPUT_BOX_X MENU_X
#define INPUT_BOX_Y MENU_H+MENU_Y-9
#define INPUT_BOX_W MENU_W-MENU_X
#define INPUT_BOX_H 25
#define INPUT_BOX_CURSOR_OFFSET 2
#define INPUT_BOX_CURSOR_WIDTH 2
#define INPUT_BOX_INIT_POS 0
#define LCD_INPUTBOX_FORECOLOR 65535
#define LCD_INPUTBOX_BACKCOLOR 63080
#define UIB_HOR_SPACING HOR_SPACING
#define RESTART yes

'------ ui_list_box ------
#define SMALL_ICON_FILE "small_icons.bmp"
#define LIST_BOX_X 25
#define LIST_BOX_Y 15
#define LIST_BOX_W 270
#define LIST_BOX_H 160
#define SCROLL_ARROW_X_POS_OFFSET 0
#define SCROLL_ARROW_Y_POS_OFFSET 2
#define ULB_VER_SPACING VER_SPACING
#define ULB_HOR_SPACING HOR_SPACING`,
        });

        blocks.push({
            location: 'boot',
            blockType: BlockType.CODE,
            template: `
    'LCD Menu
    '================================================================
            
    'turn on backlight
    io.num=PL_IO_NUM_54 
    io.state=LOW

    'call this to enter menu
    'entering_menu()
    '-----------------------------------------------------------------`,
        });


        return blocks;
    }

    getDetails(): string {
        return 'https://docs.tibbo.com/taiko/sim_lcd_obj';
    }
}
